import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FiUpload } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import { useDispatch } from "react-redux";
import { updateStaff, fetchStaff} from "../../redux/actions/staffActions"; // Import the updateStaff action

export default function EditPopup({ isOpen, onClose, data }) {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    name: "",
    designation: "",
    department: "",
    email: "",
    qualifications: "",
    file: null, // For new image
    imagePreview: null, // Preview of new image
    image_url: "",
  });

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name,
        designation: data.designation,
        department: data.department,
        email: data.email,
        qualifications: data.qualifications || "",
        file: null, // Reset file on data change
        imagePreview: null, // Reset image preview
        image_url: data.image_url || "", // For preview
      });
    }
  }, [data]);

  if (!isOpen) return null;

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      [{ font: [] }],
      ["bold", "italic", "underline"],
      [{ align: [] }],
      ["clean"],
    ],
  };

  const formats = ["font", "header", "bold", "italic", "underline", "align"];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQualificationsChange = (value) => {
    setFormData((prev) => ({ ...prev, qualifications: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          file, // Store the selected file
          imagePreview: reader.result, // Store the image preview URL
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const stripHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert("Name is required.");
      return;
    }
    if (!formData.designation.trim()) {
      alert("Designation is required.");
      return;
    }
    if (!formData.email.trim()) {
      alert("Email is required.");
      return;
    }
    const plainMessage = stripHtml(formData.qualifications);
  
        if (!plainMessage.trim()) {
          alert("Qualifications cannot be empty.");
          return;
        }
  
    const formDataToSend = new FormData();
    formDataToSend.append("name", formData.name);
    formDataToSend.append("designation", formData.designation);
    formDataToSend.append("department", formData.department);
    formDataToSend.append("email", formData.email);
    formDataToSend.append("qualifications", formData.qualifications);
  
    formDataToSend.append("file", formData.file); // Add new image as binary
  
    dispatch(updateStaff(formDataToSend, data.id)); // Dispatch action with FormData
    
    alert("Staff member updated successfully!"); // Show alert
    onClose(); // Close the popup after update
    dispatch(fetchStaff()); 
  };
  

  return (
    <div className="fixed top-0 left-0 z-50 flex items-center justify-center w-full h-full bg-black bg-opacity-50">
      <div className="w-[1100px] h-[600px] p-8 bg-white rounded-lg shadow-lg overflow-y-auto relative">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold text-gray-700">Edit Academic Staff</h2>
          <button
            type="button"
            className="text-gray-500 border rounded-md hover:text-gray-700"
            onClick={onClose}
          >
            <IoClose className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Name and Designation */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-600">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-600">Designation</label>
              <input
                type="text"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
              />
            </div>
          </div>

          {/* Contact Email */}
          <div className="mb-6">
            <label className="block mb-1 text-sm font-medium text-gray-600">Contact Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-1/2 px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
            />
          </div>

          {/* Image Upload and Preview */}
          <div className="grid grid-cols-3 gap-6 mb-6">
            <div className="flex flex-col">
              <div className="flex flex-col items-center justify-center w-full h-[225px] p-5 border border-gray-300 border-dashed rounded-md relative">
                <input
                  id="fileInput"
                  type="file"
                  name="file"
                  accept="image/*"
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  onChange={handleImageChange}
                />
                {(formData.imagePreview || formData.image_url) ? (
                  <div className="relative w-full h-full">
                    <img
                      src={formData.imagePreview || formData.image_url}
                      alt="Preview"
                      className="object-contain w-full h-full rounded-md opacity-60" // Reduced opacity
                    />
                    <div className="absolute top-0 left-0 flex items-center justify-center w-full h-full gap-2 bg-opacity-50">
                      <button
                        type="button"
                        className="px-4 py-2 bg-white border rounded-md text-primary-color border-primary-color"
                        onClick={() => document.getElementById('fileInput').click()} // Trigger input click
                      >
                        Browse Image
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <FiUpload className="w-10 h-10 p-2 mb-3 text-gray-400 bg-gray-100 border-none rounded-md" />
                    <p className="text-black">Drag and drop image here</p>
                    <p className="text-gray-400">or</p>
                    <button
                      type="button"
                      className="px-4 py-2 mt-2 text-[#92288C] font-semibold bg-[#92288C] bg-opacity-10 rounded-md"
                      onClick={() => document.getElementById('fileInput').click()} // Trigger input click
                    >
                      Browse Image
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Qualifications */}
            <div className="col-span-2">
              <label className="block mb-1 text-sm font-medium text-gray-600">Qualifications</label>
              <ReactQuill
                value={formData.qualifications}
                onChange={handleQualificationsChange}
                theme="snow"
                modules={modules}
                formats={formats}
                className="h-[180px]"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              className="px-6 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              onClick={onClose}
            >
              Back
            </button>
            <button
              type="submit"
              className="px-6 py-2 text-white bg-[#92288C] rounded-md hover:bg-[#6C0B67]"
            >
              Update
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
